import React, { useRef, useState } from 'react';
import { Alert, Box, Button, CardMedia, LinearProgress } from '@mui/material';
import { Delete, Upload } from '@mui/icons-material';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../../firebase';
import { Network, networksService } from '../../services/firebaseFunctions';

interface Props {
  network: Network;
  onChanged: (network: Network) => void;
  disabled?: boolean;
}

const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'] as const;
const maximumImageSize = 5 * 1024 * 1024;

const NetworkImageUpload: React.FC<Props> = ({ network, onChanged, disabled }) => {
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number>();
  const [error, setError] = useState<string>();

  const uploadFile = (file: File, stagingPath: string) => {
    const task = uploadBytesResumable(ref(storage, stagingPath), file, { contentType: file.type });
    return new Promise<void>((resolve, reject) => {
      task.on(
        'state_changed',
        (snapshot) => setProgress(Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100)),
        reject,
        resolve,
      );
    });
  };

  const upload = async (file?: File) => {
    if (!file) return;

    const contentType = file.type as typeof acceptedTypes[number];
    if (!acceptedTypes.includes(contentType) || file.size > maximumImageSize) {
      setError('Use JPEG, PNG ou WebP de até 5 MB.');
      return;
    }

    try {
      setError(undefined);
      setProgress(0);

      for (let attempt = 0; attempt < 2; attempt += 1) {
        let grant = await networksService.prepareImageUpload(network.id, contentType, file.size);
        if (Date.parse(grant.expiresAt) <= Date.now()) {
          grant = await networksService.prepareImageUpload(network.id, contentType, file.size);
        }

        await uploadFile(file, grant.stagingPath);
        try {
          const result = await networksService.finalizeImageUpload(grant.uploadId);
          onChanged(result.network);
          return;
        } catch (caught) {
          const expired = (caught as { code?: string }).code === 'failed-precondition';
          if (!expired || attempt === 1) throw caught;
        }
      }
    } catch (caught) {
      const code = (caught as { code?: string }).code;
      setError(code === 'failed-precondition'
        ? 'O grant expirou ou já foi consumido. Selecione a imagem novamente.'
        : 'Não foi possível concluir o upload da imagem. A Rede foi preservada sem alteração.');
    } finally {
      setProgress(undefined);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async () => {
    try {
      const result = await networksService.deleteImage(network.id);
      onChanged(result.network);
    } catch {
      setError('Não foi possível remover a imagem.');
    }
  };

  return <Box sx={{ mt: 2 }}>
    {network.imageUrl && <CardMedia component="img" image={network.imageUrl} alt="Imagem da rede" sx={{ maxWidth: 240, maxHeight: 160, objectFit: 'contain', mb: 1 }} />}
    <input ref={input} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void upload(event.target.files?.[0])} />
    <Button startIcon={<Upload />} onClick={() => input.current?.click()} disabled={disabled || progress !== undefined}>Imagem da Rede</Button>
    {network.imageUrl && <Button color="error" startIcon={<Delete />} onClick={() => void remove()} disabled={disabled || progress !== undefined}>Remover imagem</Button>}
    {progress !== undefined && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
  </Box>;
};

export default NetworkImageUpload;
